import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * ScrollableBox - A reusable scrollable container with dynamic padding
 * Automatically adds padding when scrollbar is visible to maintain spacing between content and scrollbar
 */
const ScrollableBox = ({
  children,
  // maxHeight = 'calc(100vh - 550px)',
  minHeight = '230px',
  width = '93%',
  gap = 3,
  paddingOnOverflow = '12px',
  scrollbarWidth = '8px',
  scrollbarTrackColor = 'rgba(0, 0, 0, 0.05)',
  scrollbarThumbColor = 'rgba(0, 0, 0, 0.2)',
  scrollbarThumbHoverColor = 'rgba(25, 118, 210, 0.4)',
  sx = {},
  ...props
}) => {
  const [hasOverflow, setHasOverflow] = useState(false);
  const boxRef = useRef(null);
  const checkOverflowTimeoutRef = useRef(null);
  const lastOverflowStateRef = useRef(false);

  // Check if content overflows - debounced to prevent re-render loops
  const checkOverflow = useCallback(() => {
    // Clear any pending check
    if (checkOverflowTimeoutRef.current) {
      clearTimeout(checkOverflowTimeoutRef.current);
    }

    // Debounce the overflow check
    checkOverflowTimeoutRef.current = setTimeout(() => {
      if (boxRef.current) {
        const isOverflowing = boxRef.current.scrollHeight > boxRef.current.clientHeight;
        // Only update state if value actually changed
        if (lastOverflowStateRef.current !== isOverflowing) {
          lastOverflowStateRef.current = isOverflowing;
          setHasOverflow(isOverflowing);
        }
      }
    }, 100);
  }, []);

  // Check overflow on mount only (not on children change to prevent loops)
  useEffect(() => {
    // Initial check after mount
    const initialCheckTimeout = setTimeout(checkOverflow, 200);

    // Use ResizeObserver to detect content size changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    if (boxRef.current) {
      resizeObserver.observe(boxRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      clearTimeout(initialCheckTimeout);
      if (checkOverflowTimeoutRef.current) {
        clearTimeout(checkOverflowTimeoutRef.current);
      }
    };
  }, [checkOverflow]);

  // Also check on window resize
  useEffect(() => {
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  return (
    <Box
      ref={boxRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap,
        width,
        minHeight,
        overflow: 'auto',
        paddingRight: hasOverflow ? paddingOnOverflow : 0,
        '&::-webkit-scrollbar': {
          width: scrollbarWidth,
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          borderRadius: '10px',
          backgroundColor: scrollbarTrackColor,
          marginTop: '5px',
          marginBottom: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: scrollbarThumbColor,
          '&:hover': {
            backgroundColor: scrollbarThumbHoverColor,
          },
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

ScrollableBox.propTypes = {
  children: PropTypes.node,
  // maxHeight: PropTypes.string,
  minHeight: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gap: PropTypes.number,
  paddingOnOverflow: PropTypes.string,
  scrollbarWidth: PropTypes.string,
  scrollbarTrackColor: PropTypes.string,
  scrollbarThumbColor: PropTypes.string,
  scrollbarThumbHoverColor: PropTypes.string,
  sx: PropTypes.object,
};

export default ScrollableBox;
